import { Component, DestroyRef, inject, OnInit, AfterViewInit, ViewChildren, QueryList, ElementRef, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { translate } from '@jsverse/transloco';
import { catchError, combineLatest, map, mergeMap, switchMap, toArray } from 'rxjs/operators';
import { BreadcrumbItem } from 'src/app/models/breadcrumb/breadcrumb-item';
import { RouterSegments } from 'src/app/models/router-segments';
import { BlueprintsService } from 'src/app/services/api/blueprints.service';
import { FeatureService } from 'src/app/services/api/feature.service';
import { InstancesService } from 'src/app/services/api/instances.service';
import { TenantService } from 'src/app/services/api/tenant.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { DateTimeFormatService } from 'src/app/services/util/date-time-format.service';
import { CustomToastrService } from 'src/app/services/util/toastr.service';
import { UrlService } from 'src/app/services/util/url.service';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { DeployStatus } from 'src/app/models/deploy/deploy-status';
import { CloudProvider } from 'src/app/models/cloud-provider/cloud-provider';
import { DataZone } from 'src/app/models/data-zone/data-zone';

@Component({
  selector: 'app-tenant-details',
  templateUrl: './tenant-details.component.html',
  styleUrl: './tenant-details.component.scss',
  standalone: false
})
export class TenantDetailsComponent implements OnInit, AfterViewInit {
  private destroyRef = inject(DestroyRef);
  breadcrumbItems: BreadcrumbItem[] = [];
  tenantId: string;

  @ViewChildren('sectionEl') sections!: QueryList<ElementRef>;
  @ViewChild('scrollContainer') scrollContainer!: ElementRef;
  @ViewChild(MatSort, {static: true}) sort;
  @ViewChild(MatSort)
  set matSort(sort: MatSort) {
    if (sort) {
      this.dataSource.sort = sort;
    }
  }

  menuItems = [
    { id: 'section1', label: 'Foundation Details' },
    { id: 'section2', label: 'Cloud Region' },
    { id: 'section3', label: 'Environment' },
    { id: 'section4', label: 'Features' },
    { id: 'section5', label: 'Settings' }
  ];

  activeSection = 'section1';
  tenantData: any;
  featureList: any;
  envData: any;
  dataSource: any;
  displayedColumns: string[] = ['name', 'dataZone', 'deploymentClass', 'blueprint', 'dateCreated', 'createdBy', 'status'];
  DataZone = DataZone;
  DeployStatus = DeployStatus;
  notificationEmails: string;
  loadEnvData = false;
  private observer!: IntersectionObserver;

  statusIcons = {
    deployed: 'check_circle_filled',
    configured: 'check_circle',
    initialized: 'check_circle',
    updated: 'check_circle',
    tested: 'check_circle',
    failed: 'cancel_filled',
    configuration_failed: 'cancel_filled',
    terminated: 'error_outline',
    termination_failed: 'cancel_filled',
    test_failed: 'cancel_filled',
    update_failed: 'cancel_filled',
    stopped: 'error_outline',
    start_failed: 'cancel_filled',
    stop_failed: 'cancel_filled',
    initialize_failed: 'cancel_filled',
    deploying: 'change_circle',
    terminating: 'change_circle',
    configuring: 'change_circle',
    updating: 'change_circle',
    initializing: 'change_circle',
    operation_inprogress: 'change_circle',
    inprogress: 'change_circle',
    stopping: 'change_circle',
    stopping_agent: 'change_circle',
    starting: 'change_circle',
    starting_agent: 'change_circle',
    testing: 'change_circle',
    fallback_inprogress: 'change_circle',
    failover_inprogress: 'change_circle',
  };

  constructor(private tenantService: TenantService, private activatedRoute: ActivatedRoute, 
    private router: Router, private authService: AuthService, private toastrService: CustomToastrService,
    private featureService: FeatureService, public urlService: UrlService, public dateTimeFormatService: DateTimeFormatService,
    private instanceService: InstancesService, private blueprintsService: BlueprintsService) {

  }

  ngOnInit(): void {
    this.tenantId = this.activatedRoute.snapshot.params.tenantId;
    this.breadcrumbItems.push({icon: 'home'},
    {label: translate('LEFT_SIDE_NAV.TENANT'), url: [RouterSegments.TENANT_LIST]},
    {label: this.tenantId}
  );

    this.tenantEnvironmentDetails();
    this.tenantDetails();
    this.tenantFeatures();
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.initObserver());
  }

  private initObserver(): void {
    const container = this.scrollContainer.nativeElement;

    this.observer = new IntersectionObserver(
      entries => {
        let closest: IntersectionObserverEntry | null = null;
        let minDistance = Number.MAX_VALUE;

        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const distance = Math.abs(entry.boundingClientRect.top);
            if (distance < minDistance) {
              minDistance = distance;
              closest = entry;
            }
          }
        });

        if (closest) {
          this.activeSection = closest.target.id;
        }
      },
      {
        root: container,
        threshold: 0,
        rootMargin: '0px 0px -60% 0px'
      }
    );

    this.sections.forEach(section =>
      this.observer.observe(section.nativeElement)
    );
    container.addEventListener('scroll', this.onScroll);
  }

  onScroll = (): void => {
    const container = this.scrollContainer.nativeElement;

    const isAtBottom =
      container.scrollTop + container.clientHeight >=
      container.scrollHeight - 5;

    if (isAtBottom) {
      const lastSection = this.sections.last;
      if (lastSection) {
        this.activeSection = lastSection.nativeElement.id;
      }
    }
  };

  ngOnDestroy(): void {
    this.observer?.disconnect();
    this.scrollContainer.nativeElement.removeEventListener('scroll', this.onScroll);
  }
  scrollTo(id: string) {
    const target = document.getElementById(id);

    if (target) {
      target.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  }

  goToTenant(): void {
    this.router.navigate(['/', this.authService.principal.provider, RouterSegments.TENANT_LIST]);
  }

  /**
  * Fetches instance details for a tenant and find them with their corresponding blueprint information.
  */
  tenantEnvironmentDetails(): void {
    const obj = { tenantId: this.tenantId };
    this.instanceService.getInstances(this.tenantId)
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe((data) => {
      if (data) {
        const req = { tenantId: this.tenantId, size: data?.total };
        this.instanceService.getInstances(req).pipe(
          takeUntilDestroyed(this.destroyRef),
          map((res: any) => res.data),
          map(instances =>
            instances.filter(inst =>
              inst.status !== DeployStatus.TERMINATED &&
              inst.status !== DeployStatus.TERMINATING &&
              inst.status !== DeployStatus.TERMINATION_FAILED
            )
          ),
          switchMap(instances =>
            this.blueprintsService.getBlueprints(this.tenantId).pipe(
              map((blueprints: any[]) => ({ instances, blueprints }))
            )
          ),

          map(({ instances, blueprints }) =>
            instances.map(inst => ({
              ...inst,
              blueprint: blueprints.find(bp => bp.id === inst.blueprintId) || null
            }))
          )

        ).subscribe({
          next: finalResult => {
            finalResult.sort(
              (a: any, b: any) => new Date(b?.dateCreated).getTime() - new Date(a?.dateCreated).getTime());
            this.envData = finalResult;
            this.dataSource = new MatTableDataSource(this.envData);
            this.dataSource.sort = this.sort;
            this.loadEnvData = true;
          },
          error: error => {
            this.loadEnvData = true;
            setTimeout(() => {
            const title = translate('ERRORS.SERVICE_AVAILABILITY');
            const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
            this.toastrService.errMessage(message, title);
          }, 500);
          }
        });


      }
    });

   
  }

/**
 * Get the tenant details
 */
  tenantDetails():void {
    this.tenantService.getTenantDetails(this.tenantId)
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe((data) => {
      if (data) {
        this.tenantData = data;
        this.notificationEmails = this.tenantData?.notificationEmails?.join(", ");
      }
    },
    (error) => {
      setTimeout(() => {
        const title = translate('ERRORS.SERVICE_AVAILABILITY');
        const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
        this.toastrService.errMessage(message, title);
      }, 500);
    });
  }

  /**
   * Get the tenant features
   */
  tenantFeatures():void {
    this.featureService.getTenantIdFeatures(this.tenantId)
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe((data) => {
      if (data) {
        this.featureList = data;
      }
    },
    (error) => {
      setTimeout(() => {
        const title = translate('ERRORS.SERVICE_AVAILABILITY');
        const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
        this.toastrService.errMessage(message, title);
      }, 500);
    });
  }

  sortData(event): void {
    this.sort = event;
    this.envData = this.sortInstanceData(event);
  }

  sortInstanceData(event): any {
    return this.envData?.sort((a, b) => {
      let comparatorResult = 0;
      switch (event.active) {
        case 'name':
          comparatorResult = a?.name && b?.name ? a?.name?.localeCompare(b?.name) : !a?.name ? -1 : 1;
          break;
        case 'dataZone':
          comparatorResult = a?.dataZone && b?.dataZone ? a?.dataZone?.localeCompare(b?.dataZone) : !a?.dataZone ? -1 : 1;
          break;
        case 'deploymentClass':
          comparatorResult = a?.deploymentClass && b?.deploymentClass ? new Date(a?.deploymentClass)?.getTime() - new Date(b?.deploymentClass)?.getTime()
            : !a?.deploymentClass ? -1 : 1;
          break;
        case 'blueprint':
          comparatorResult = a?.blueprint.name && b?.blueprint.name ? a?.blueprint.name?.localeCompare(b?.blueprint.name) : !a?.blueprint.name ? -1 : 1;
          break;
        
        case 'dateCreated':
          comparatorResult = a?.dateCreated && b?.dateCreated ? a?.dateCreated?.localeCompare(b?.dateCreated)
            : !a?.dateCreated ? -1 : 1;
          break;
        case 'createdBy':
          comparatorResult = a?.createdBy && b?.createdBy ? a?.createdBy?.localeCompare(b?.createdBy)
            : !a?.createdBy ? -1 : 1;
          break;
        case 'status':
          comparatorResult = a?.status && b?.status ? a?.status?.localeCompare(b?.status)
            : !a?.status ? -1 : 1;
          break;
        default:
          comparatorResult = a?.dateCreated && b?.dateCreated ? a?.dateCreated?.localeCompare(b?.dateCreated)
            : !a?.dateCreated ? -1 : 1;
          break;
      }
      return comparatorResult * (event.direction === 'asc' ? 1 : -1);
     });
  }

/**
 * open instance page
 * @param data instance data
 */
  openInstancePage(data): void {
    const cloudProvider = this.tenantData?.targetCloud === CloudProvider.AWS ? 'environment' : 'instance';
    this.router.navigate(['/', this.authService.principal.provider, RouterSegments.TENANT, data.tenantId,
      RouterSegments.BLUEPRINTS, data.blueprintId, cloudProvider, data.id
    ]);
  }

  getZoneValue(feature?: any, zone?: string): boolean {
    const val = feature.dataZones?.find(z => z?.dataZone === zone)?.enabled;
    return val === 'true' || val === true;
  }

  getZoneModifiable(feature?: any, zone?: string): boolean {
    const val = feature.dataZones?.find(z => z?.dataZone === zone)?.modifiable;
    return val === 'true' || val === true;
  }

  openUrl(): void {
    this.urlService.openUrlInNewTab(`https://keycloak.shs.saas.temenos.cloud/admin/${this.tenantData.tenantId}/console/`);
  }

}
